import { useMemo } from 'react';
import { C } from '../constants';
import { formatNumber, safePct } from '../utils';
import Glass from './Glass';

export default function StateResults({ counts, stateData }) {
  const { parties } = stateData;

  const totals = useMemo(() => {
    const t = Object.fromEntries(parties.map(p => [p.id, 0]));
    Object.values(counts).forEach(d => {
      Object.entries(d).forEach(([pid, c]) => { t[pid] = (t[pid] || 0) + c; });
    });
    return t;
  }, [counts, parties]);

  const grand  = Object.values(totals).reduce((a, b) => a + b, 0);
  const sorted = [...parties].sort((a, b) => totals[b.id] - totals[a.id]);
  const leader = sorted[0];

  return (
    <Glass style={{ padding: '1.6rem' }}>
      <div
        className="flex items-end justify-between mb-5 pb-4 flex-wrap gap-3"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.textDim, fontWeight: 600 }}>
            Statewide ·{' '}
            <span style={{ fontFamily: stateData.localFont, textTransform: 'none', letterSpacing: 0 }}>
              {stateData.strings.statewide}
            </span>
          </div>
          <div className="flex items-baseline gap-3 mt-1.5">
            <span style={{
              fontFamily: "'Poppins', sans-serif", fontWeight: 700,
              fontSize: '2.8rem', lineHeight: 1, color: C.white,
              letterSpacing: '-0.02em', textShadow: '0 0 24px rgba(0,229,255,0.3)',
            }}>
              {formatNumber(grand)}
            </span>
            <span style={{ fontSize: '0.85rem', color: C.textDim }}>total votes</span>
          </div>
        </div>

        {grand > 0 && (
          <div className="text-right">
            <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.textDim, fontWeight: 600 }}>
              Leading
            </div>
            <div className="flex items-center gap-2 justify-end mt-1.5">
              <span style={{ width: 12, height: 12, backgroundColor: leader.color, borderRadius: 2, boxShadow: `0 0 12px ${leader.color}`, display: 'inline-block' }} />
              <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1.4rem', color: C.white }}>
                {leader.en}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {sorted.map((p, i) => {
          const v   = totals[p.id] || 0;
          const pct = safePct(v, grand);
          return (
            <div key={p.id} className="flex items-center gap-3">
              <span style={{ width: 22, fontStyle: 'italic', color: C.textDimmer, fontSize: '0.78rem', textAlign: 'right' }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="flex-1">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="flex items-baseline gap-2">
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white }}>{p.en}</span>
                    <span style={{ fontFamily: stateData.localFont, fontSize: '0.72rem', color: C.textDim }}>{p.local}</span>
                  </span>
                  <span className="flex items-baseline gap-2">
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white }}>{pct.toFixed(1)}%</span>
                    <span style={{ fontSize: '0.72rem', color: C.textDimmer }}>({formatNumber(v)})</span>
                  </span>
                </div>
                <div style={{ height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${pct}%`,
                    backgroundColor: p.color, borderRadius: 4,
                    boxShadow: `0 0 12px ${p.color}99`,
                    transition: 'width .8s cubic-bezier(.2,.7,.2,1)',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Glass>
  );
}
